import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { matchSorter } from 'match-sorter';
import { Column, DataTable, Page, PageEmptyState, PageErrorState, Row, Searchbox, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { EmptyStateIllustration } from '~/components/EmptyStateIllustration/EmptyStateIllustration';
import PageLoadingState from '~/components/PageLoadingState';
import { useContactSummaries } from '~/hooks/reactQuery/queries/useContactSummaries';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useLocaleKeys } from '~/i18n';
import { contactRowActions } from './ContactsDashboard.rowActions';
import { CONTACTS_COLUMN_NAMES, DEFAULT_CONTACTS_SORT, contactsColumns, sortContactsByColumn, toContactsRows } from './ContactsDashboard.table.utils';
import { SEARCHABLE_KEYS } from './ContactsDashboard.utils';
import { useAddContactToClientModal } from './hooks/useAddContactToClientModal';
import { useDeleteContactModal } from './hooks/useDeleteContactModal';
import { useEditContactIdentityModal } from './hooks/useEditContactIdentityModal';
import { useOpenContactClientPortal } from './hooks/useOpenContactClientPortal';
export const GetContactsDashboardTID = (parentTestId)=>getTestIds(parentTestId);
const MOBILE_FIELDS = [
    {
        name: 'contactName'
    },
    {
        name: 'email'
    },
    {
        name: 'phone'
    },
    {
        name: 'associatedClients'
    }
];
export const ContactsDashboard = ()=>{
    const { ContactsDashboardTestIds } = useMemo(()=>GetContactsDashboardTID(), []);
    const { t } = useLocaleKeys('client.contactsDashboard');
    const [search, setSearch] = useState('');
    const { contacts, isLoading, error, refresh } = useContactSummaries();
    const defaultSort = useMemo(()=>({
            colIndex: CONTACTS_COLUMN_NAMES.indexOf(DEFAULT_CONTACTS_SORT.name),
            sort: DEFAULT_CONTACTS_SORT.direction
        }), []);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds: CONTACTS_COLUMN_NAMES
    });
    const openEditModal = useEditContactIdentityModal(ContactsDashboardTestIds.EditModal());
    const openDeleteModal = useDeleteContactModal(ContactsDashboardTestIds.DeleteModal());
    const openClientPortal = useOpenContactClientPortal();
    const openAddToClientModal = useAddContactToClientModal(ContactsDashboardTestIds.AddToClientModal());
    useSetDocumentTitle(t.title());
    const tableSort = useMemo(()=>{
        var _CONTACTS_COLUMN_NAMES_, _ref;
        return {
            name: (_CONTACTS_COLUMN_NAMES_ = CONTACTS_COLUMN_NAMES[(_ref = sort === null || sort === void 0 ? void 0 : sort.colIndex) !== null && _ref !== void 0 ? _ref : defaultSort.colIndex]) !== null && _CONTACTS_COLUMN_NAMES_ !== void 0 ? _CONTACTS_COLUMN_NAMES_ : DEFAULT_CONTACTS_SORT.name,
            direction: (sort === null || sort === void 0 ? void 0 : sort.sort) === 'desc' ? 'desc' : 'asc'
        };
    }, [
        sort === null || sort === void 0 ? void 0 : sort.colIndex,
        sort === null || sort === void 0 ? void 0 : sort.sort,
        defaultSort.colIndex
    ]);
    const visibleContacts = useMemo(()=>{
        const matched = search.length === 0 ? contacts : matchSorter(contacts, search, {
            keys: SEARCHABLE_KEYS
        });
        return sortContactsByColumn(matched, tableSort);
    }, [
        contacts,
        search,
        tableSort
    ]);
    const [mainColumn, ...columns] = useMemo(()=>contactsColumns(t), []);
    const columnMenu = useTableColumnMenu('contacts');
    if (isLoading) return /*#__PURE__*/ _jsx(PageLoadingState, {});
    if (error != null) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: ContactsDashboardTestIds.ErrorState(),
            title: t.errorState.title(),
            message: t.errorState.description(),
            onRefresh: refresh,
            pullToRefresh: true
        });
    }
    const hasNoContacts = contacts.length === 0;
    const contactById = new Map(contacts.map((contact)=>[
            contact.contactId,
            contact
        ]));
    return /*#__PURE__*/ _jsx(Page, {
        testId: ContactsDashboardTestIds.Root(),
        variant: "containedHeight",
        onRefresh: refresh,
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: 16,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h5",
                    weight: "normal",
                    children: t.title()
                }),
                /*#__PURE__*/ _jsx(Row, {
                    children: /*#__PURE__*/ _jsx(Searchbox, {
                        testId: ContactsDashboardTestIds.Search(),
                        placeholder: t.searchPlaceholder(),
                        value: search,
                        onChange: (event)=>setSearch(event.target.value),
                        onClear: ()=>setSearch(''),
                        bi: {
                            name: 'contacts_search'
                        }
                    })
                }),
                hasNoContacts && /*#__PURE__*/ _jsx(PageEmptyState, {
                    maxHeight: true,
                    testId: ContactsDashboardTestIds.EmptyState(),
                    illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                    title: t.emptyState.title(),
                    message: t.emptyState.description()
                }),
                !hasNoContacts && visibleContacts.length === 0 && /*#__PURE__*/ _jsx(PageEmptyState, {
                    maxHeight: true,
                    testId: ContactsDashboardTestIds.NoSearchResults(),
                    illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                    title: t.noSearchResults.title(),
                    message: t.noSearchResults.description({
                        search
                    })
                }),
                !hasNoContacts && /*#__PURE__*/ _jsx(DataTable, {
                    mainColumn: mainColumn,
                    allowColumnResize: true,
                    allowColumnReorder: true,
                    reorderBi: {
                        name: 'contacts_reorder_columns'
                    },
                    allowColumnHide: true,
                    columnMenu: columnMenu,
                    columns: columns,
                    items: toContactsRows(visibleContacts),
                    getRowId: (row)=>row.id,
                    mobile: {
                        fields: MOBILE_FIELDS
                    },
                    sort: {
                        name: tableSort.name,
                        direction: tableSort.direction,
                        sortedBy: 'consumer',
                        onChange: (nextSort)=>changeSort({
                                colIndex: CONTACTS_COLUMN_NAMES.indexOf(nextSort.name),
                                sort: nextSort.direction
                            })
                    },
                    rowActions: {
                        menuBi: {
                            name: 'contact_actions'
                        },
                        menuTitle: (row)=>row.contactName,
                        menu: (row)=>{
                            const contact = contactById.get(row.id);
                            return contact == null ? [] : contactRowActions({
                                contact,
                                texts: t,
                                onEdit: openEditModal,
                                onAddToAnotherClient: openAddToClientModal,
                                onDelete: openDeleteModal,
                                onViewClientPortal: openClientPortal
                            });
                        }
                    },
                    testId: ContactsDashboardTestIds.Table()
                })
            ]
        })
    });
};
